import { useEffect, useMemo, useState } from 'react'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FormInputField } from '@/components/careers/CareersFormFields'
import { applicationConfig, vacancies } from '../../pages/careers/careersData'

const initialFormState = {
  firstName: '',
  surname: '',
  email: '',
  telephone: '',
  position: '',
}

const NAME_REGEX = /^[A-Za-z\s'-]+$/
const PHONE_REGEX = /^[+]?[\d\s()-]{7,15}$/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const PERSONAL_DETAIL_FIELDS = [
  {
    id: 'firstName',
    label: 'First name',
    autoComplete: 'given-name',
  },
  {
    id: 'surname',
    label: 'Surname',
    autoComplete: 'family-name',
  },
  {
    id: 'email',
    label: 'Email address',
    type: 'email',
    autoComplete: 'email',
  },
  {
    id: 'telephone',
    label: 'Telephone number',
    type: 'tel',
    autoComplete: 'tel',
  },
]

const validateField = (name, value) => {
  switch (name) {
    case 'firstName':
    case 'surname': {
      if (!value || !value.trim()) return 'This field is required.'

      if (!NAME_REGEX.test(value)) {
        return 'Only letters, spaces, hyphens and apostrophes are allowed.'
      }

      if (value.trim().length < 2) return 'Must be at least 2 characters.'

      return ''
    }

    case 'email': {
      if (!value || !value.trim()) return 'This field is required.'

      if (!EMAIL_REGEX.test(value.trim())) {
        return 'Please enter a valid email address.'
      }

      return ''
    }

    case 'telephone': {
      if (!value || !value.trim()) return 'This field is required.'

      if (!PHONE_REGEX.test(value.trim())) {
        return 'Please enter a valid telephone number (digits, spaces, +, -, () only).'
      }

      return ''
    }

    case 'position': {
      if (!value || !value.trim()) return 'This field is required.'

      return ''
    }

    default:
      return ''
  }
}

const buildApplicationMailtoUrl = ({ formData, selectedVacancy }) => {
  const { companyName, hrEmail } = applicationConfig
  const company = selectedVacancy?.company || companyName
  const firstName = formData.firstName.trim()
  const surname = formData.surname.trim()
  const position = formData.position.trim()

  const subject = `Application : ${position} - ${firstName} ${surname}`

  const body = [
    'Hello,',
    '',
    `I would like to apply for the position of ${position} at ${company}.`,
    '',
    `First name: ${firstName}`,
    `Surname: ${surname}`,
    `Email: ${formData.email.trim()}`,
    `Telephone: ${formData.telephone.trim()}`,
    '',
    'Please find my CV attached.',
    '',
    'Regards,',
    `${firstName} ${surname}`,
  ].join('\r\n')

  return `mailto:${hrEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

const CareersApplicationForm = ({ formRef, selectedApplication }) => {
  const [formData, setFormData] = useState(initialFormState)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [errors, setErrors] = useState({})

  const selectedVacancy = useMemo(
    () => vacancies.find(vacancy => vacancy.title === formData.position),
    [formData.position]
  )

  useEffect(() => {
    if (!selectedApplication?.position) return

    setFormData(current => ({
      ...current,
      position: selectedApplication.position,
    }))

    setSubmitStatus(null)
  }, [selectedApplication])

  const handleInputChange = event => {
    const { name, value } = event.target

    setFormData(current => ({
      ...current,
      [name]: value,
    }))

    setErrors(current => ({
      ...current,
      [name]: validateField(name, value),
    }))
  }

  const resetForm = () => {
    setFormData(initialFormState)
    setErrors({})
  }

  const validateForm = () => {
    const newErrors = {}

    Object.keys(initialFormState).forEach(field => {
      const error = validateField(field, formData[field])

      if (error) {
        newErrors[field] = error
      }
    })

    setErrors(newErrors)

    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = event => {
    event.preventDefault()
    setSubmitStatus(null)

    if (!validateForm()) {
      setSubmitStatus('Please fix the errors below and try again.')

      return
    }

    window.location.href = buildApplicationMailtoUrl({
      formData,
      selectedVacancy,
    })

    resetForm()
  }

  return (
    <section className="bg-gray-100 py-14 md:py-18">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6">
        <div className="mb-8">
          <h2 className="text-3xl font-black md:text-4xl">Application Form</h2>
          <p className="text-foreground/80 mt-3 max-w-2xl text-base leading-relaxed">
            Submit your details and CV for a current vacancy or a future
            opportunity.
          </p>
        </div>

        <form
          ref={formRef}
          id="careers-application-form"
          onSubmit={handleSubmit}
          className="text-foreground rounded-md bg-white p-5 shadow-xl md:p-8"
        >
          <div className="grid gap-5 md:grid-cols-2">
            {PERSONAL_DETAIL_FIELDS.map(field => (
              <FormInputField
                key={field.id}
                id={field.id}
                label={field.label}
                type={field.type}
                autoComplete={field.autoComplete}
                value={formData[field.id]}
                onChange={handleInputChange}
                required
                error={errors[field.id]}
              />
            ))}

            <FormInputField
              id="position"
              label="Position applied for"
              list="careers-position-options"
              value={formData.position}
              onChange={handleInputChange}
              required
              error={errors.position}
              wrapperClassName="md:col-span-2"
            >
              <datalist id="careers-position-options">
                {vacancies.map(vacancy => (
                  <option key={vacancy.id} value={vacancy.title} />
                ))}

                <option value="Speculative application" />
              </datalist>
            </FormInputField>
          </div>

          {submitStatus && (
            <div className="mt-5 rounded-md bg-red-50 px-4 py-3 text-sm font-bold text-red-800">
              {submitStatus}
            </div>
          )}

          <div className="mt-8 flex justify-end">
            <Button
              type="submit"
              size="lg"
              className="h-12 cursor-pointer px-7 text-base font-black"
            >
              Submit
              <Send className="size-5" aria-hidden="true" />
            </Button>
          </div>
        </form>
      </div>
    </section>
  )
}

export default CareersApplicationForm
